import { argon2id } from '@noble/hashes/argon2.js';

export const MSEED_MAGIC = "MAPLSEED";
export const MSEED_VERSION = 1;
const HEADER_PREFIX_BYTES = 24;
const PASSWORD_VERIFIER_SECRET = "MAPLEOS_MSEED_PASSWORD_GATE_V1";

export type MSeedBlockKind = "text" | "image" | "vector" | "json" | "blueprint";

export type MSeedTextBlock = {
  id: string;
  cid: string;
  kind: "text";
  name: string;
  mediaType: "text/plain" | "text/markdown";
  text: string;
  byteLength: number;
};

export type MSeedImageBlock = {
  id: string;
  cid: string;
  kind: "image";
  name: string;
  mediaType: string;
  dataUrl: string;
  byteLength: number;
};

export type MSeedVectorBlock = {
  id: string;
  cid: string;
  kind: "vector";
  name: string;
  mediaType: "application/json";
  values: number[];
  dimensions: number;
  byteLength: number;
};

export type MSeedJsonBlock = {
  id: string;
  cid: string;
  kind: "json" | "blueprint";
  name: string;
  mediaType: "application/json";
  value: unknown;
  byteLength: number;
};

export type MSeedBlock =
  | MSeedTextBlock
  | MSeedImageBlock
  | MSeedVectorBlock
  | MSeedJsonBlock;

export type MSeedBehavior = {
  id: string;
  cid: string;
  kind: "wasm";
  name: string;
  mediaType: "application/wasm";
  sourceFormat: "wat" | "wasm";
  wasmBase64: string;
  sourceText?: string;
  exportsHint?: string[];
  byteLength: number;
};

export type MSeedProofEntry = {
  seq: number;
  blockId: string;
  cid: string;
  kind: string;
  prev: string;
  hash: string;
};

export type MSeedSeal = {
  algorithm: "HMAC-SHA-256";
  keyId: string;
  signedAt: string;
  payload: string;
  signature: string;
};

export type MSeedManifest = {
  seedId: string;
  title: string;
  summary: string;
  createdAt: string;
  dossierStyle: "tabs";
  author: string;
  tags: string[];
  source: "manual" | "blueprint" | "hybrid";
  blockCount: number;
  hasBehavior: boolean;
};

export type MSeedPayload = {
  manifest: MSeedManifest;
  blocks: MSeedBlock[];
  behavior: MSeedBehavior | null;
  proof: {
    manifestHash: string;
    chain: MSeedProofEntry[];
  };
  dossier: {
    sections: Array<"overview" | "data" | "behavior" | "proof">;
  };
};

export type MSeedPasswordKdf = {
  name: "Argon2id";
  saltBase64: string;
  memoryKiB: number;
  iterations: number;
  parallelism: number;
  version: number;
};

export type MSeedPasswordVerifier = {
  algorithm: "AES-256-GCM";
  ivBase64: string;
  ciphertextBase64: string;
};

export type MSeedPasswordProtection = {
  enabled: true;
  kdf: MSeedPasswordKdf;
  verifier: MSeedPasswordVerifier;
};

export type MSeedHeader = {
  format: "mseed";
  version: number;
  seedId: string;
  createdAt: string;
  manifestHash: string;
  envelope: {
    algorithm: "AES-256-GCM";
    keyWrap: "RSA-OAEP-256";
    ivBase64: string;
    wrappedKeyBase64: string;
    keyId: string;
  };
  passwordProtection?: MSeedPasswordProtection;
  seal: MSeedSeal;
};

export type ParsedMSeed = {
  header: MSeedHeader;
  ciphertext: Uint8Array;
  bytes: Uint8Array;
};

export type ProofVerificationResult = {
  valid: boolean;
  manifestHashMatches: boolean;
  chainValid: boolean;
  errors: string[];
};

export const DEFAULT_MSEED_PASSWORD_KDF = {
  name: "Argon2id",
  memoryKiB: 19456,
  iterations: 2,
  parallelism: 1,
  version: 19,
} satisfies Omit<MSeedPasswordKdf, "saltBase64">;

function encodeUtf8(value: string) {
  return new TextEncoder().encode(value);
}

function decodeUtf8(value: Uint8Array) {
  return new TextDecoder().decode(value);
}

function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer;
}

function normalizeForStableJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeForStableJson);
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeForStableJson((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }

  return value;
}

function normalizePassword(password: string) {
  return password.normalize("NFKC");
}

async function importAesKey(rawKey: Uint8Array, usages: KeyUsage[]) {
  return crypto.subtle.importKey("raw", toArrayBuffer(rawKey), { name: "AES-GCM" }, false, usages);
}

async function decryptPasswordVerifierBytes(passwordProtection: MSeedPasswordProtection, password: string) {
  const keyBytes = await derivePasswordKey(password, passwordProtection.kdf);
  const aesKey = await importAesKey(keyBytes, ["decrypt"]);
  const iv = base64ToBytes(passwordProtection.verifier.ivBase64);
  const ciphertext = base64ToBytes(passwordProtection.verifier.ciphertextBase64);
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, aesKey, toArrayBuffer(ciphertext));
  return new Uint8Array(decrypted);
}

export function stableStringify(value: unknown) {
  return JSON.stringify(normalizeForStableJson(value));
}

export async function sha256Hex(input: string | Uint8Array) {
  const bytes = typeof input === "string" ? encodeUtf8(input) : input;
  const hash = await crypto.subtle.digest("SHA-256", toArrayBuffer(bytes));
  return bytesToHex(new Uint8Array(hash));
}

export function dataUrlToMimeType(dataUrl: string) {
  const match = dataUrl.match(/^data:([^;]+);base64,/i);
  return match?.[1] || "application/octet-stream";
}

export function bytesToBase64(bytes: Uint8Array) {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
}

export function base64ToBytes(value: string) {
  const clean = value.trim();
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export function decodeStoredBinaryContent(content: string) {
  if (!content) {
    return new Uint8Array();
  }

  if (content.startsWith("data:")) {
    const [, payload = ""] = content.split(",");
    return base64ToBytes(payload);
  }

  return base64ToBytes(content);
}

export function downloadBinaryFile(bytes: Uint8Array, filename: string, type = "application/octet-stream") {
  const blob = new Blob([toArrayBuffer(bytes)], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function concatBytes(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  parts.forEach((part) => {
    combined.set(part, offset);
    offset += part.length;
  });
  return combined;
}

export function seedHasPasswordProtection(header: Pick<MSeedHeader, "passwordProtection"> | null | undefined) {
  return Boolean(header?.passwordProtection?.enabled);
}

export async function derivePasswordKey(password: string, kdf: MSeedPasswordKdf) {
  const normalized = normalizePassword(password);
  const salt = base64ToBytes(kdf.saltBase64);
  return Uint8Array.from(argon2id(encodeUtf8(normalized), salt, {
    t: kdf.iterations,
    m: kdf.memoryKiB,
    p: kdf.parallelism,
    dkLen: 32,
    version: kdf.version,
  }));
}

export async function createPasswordProtection(password: string): Promise<MSeedPasswordProtection> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const kdf: MSeedPasswordKdf = {
    ...DEFAULT_MSEED_PASSWORD_KDF,
    saltBase64: bytesToBase64(salt),
  };

  const keyBytes = await derivePasswordKey(password, kdf);
  const aesKey = await importAesKey(keyBytes, ["encrypt"]);
  const verifierPlaintext = encodeUtf8(stableStringify({
    secret: PASSWORD_VERIFIER_SECRET,
    issuedAt: new Date().toISOString(),
  }));

  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, verifierPlaintext);

  return {
    enabled: true,
    kdf,
    verifier: {
      algorithm: "AES-256-GCM",
      ivBase64: bytesToBase64(iv),
      ciphertextBase64: bytesToBase64(new Uint8Array(encrypted)),
    },
  };
}

export async function verifyPasswordProtection(passwordProtection: MSeedPasswordProtection, password: string) {
  try {
    const decrypted = await decryptPasswordVerifierBytes(passwordProtection, password);
    const parsed = JSON.parse(decodeUtf8(decrypted)) as { secret?: string };
    return parsed.secret === PASSWORD_VERIFIER_SECRET;
  } catch {
    return false;
  }
}

export async function createContentAddress(kind: string, payload: unknown) {
  return sha256Hex(`${kind}:${stableStringify(payload)}`);
}

export async function makeTextBlock(name: string, text: string, mediaType: "text/plain" | "text/markdown" = "text/markdown"): Promise<MSeedTextBlock> {
  const payload = { name, text, mediaType };
  const cid = await createContentAddress("text", payload);
  return {
    id: `blk-${cid.slice(0, 12)}`,
    cid,
    kind: "text",
    name,
    mediaType,
    text,
    byteLength: encodeUtf8(text).length,
  };
}

export async function makeImageBlock(name: string, dataUrl: string): Promise<MSeedImageBlock> {
  const payload = { name, dataUrl };
  const cid = await createContentAddress("image", payload);
  return {
    id: `blk-${cid.slice(0, 12)}`,
    cid,
    kind: "image",
    name,
    mediaType: dataUrlToMimeType(dataUrl),
    dataUrl,
    byteLength: Math.ceil(dataUrl.length * 0.75),
  };
}

export async function makeVectorBlock(name: string, values: number[]): Promise<MSeedVectorBlock> {
  const payload = { name, values };
  const cid = await createContentAddress("vector", payload);
  return {
    id: `blk-${cid.slice(0, 12)}`,
    cid,
    kind: "vector",
    name,
    mediaType: "application/json",
    values,
    dimensions: values.length,
    byteLength: encodeUtf8(JSON.stringify(values)).length,
  };
}

export async function makeJsonBlock(name: string, value: unknown, kind: "json" | "blueprint" = "json"): Promise<MSeedJsonBlock> {
  const payload = { name, value, kind };
  const cid = await createContentAddress(kind, payload);
  return {
    id: `blk-${cid.slice(0, 12)}`,
    cid,
    kind,
    name,
    mediaType: "application/json",
    value,
    byteLength: encodeUtf8(stableStringify(value)).length,
  };
}

export async function makeBehaviorBlock(name: string, wasmBytes: Uint8Array, sourceFormat: "wat" | "wasm", sourceText?: string): Promise<MSeedBehavior> {
  const wasmBase64 = bytesToBase64(wasmBytes);
  const payload = { name, wasmBase64, sourceFormat, sourceText: sourceText || "" };
  const cid = await createContentAddress("behavior", payload);
  return {
    id: `beh-${cid.slice(0, 12)}`,
    cid,
    kind: "wasm",
    name,
    mediaType: "application/wasm",
    sourceFormat,
    wasmBase64,
    sourceText,
    exportsHint: ["run", "add"],
    byteLength: wasmBytes.length,
  };
}

export async function buildProofChain(blocks: MSeedBlock[], behavior: MSeedBehavior | null) {
  const entries = [...blocks, ...(behavior ? [behavior] : [])];
  const chain: MSeedProofEntry[] = [];
  let previousHash = "0".repeat(64);

  for (let index = 0; index < entries.length; index += 1) {
    const current = entries[index];
    const recordBase = {
      seq: index,
      blockId: current.id,
      cid: current.cid,
      kind: current.kind,
      prev: previousHash,
    };
    const hash = await sha256Hex(stableStringify(recordBase));
    chain.push({ ...recordBase, hash });
    previousHash = hash;
  }

  return chain;
}

export async function createPayloadManifestHash(payload: Omit<MSeedPayload, "proof">, chain: MSeedProofEntry[]) {
  return sha256Hex(stableStringify({
    manifest: payload.manifest,
    blocks: payload.blocks,
    behavior: payload.behavior,
    dossier: payload.dossier,
    chain,
  }));
}

export async function verifyProof(payload: MSeedPayload, expectedManifestHash: string): Promise<ProofVerificationResult> {
  const errors: string[] = [];
  let previousHash = "0".repeat(64);

  for (let index = 0; index < payload.proof.chain.length; index += 1) {
    const entry = payload.proof.chain[index];
    const expectedHash = await sha256Hex(stableStringify({
      seq: entry.seq,
      blockId: entry.blockId,
      cid: entry.cid,
      kind: entry.kind,
      prev: entry.prev,
    }));

    if (entry.prev !== previousHash) {
      errors.push(`Proof chain break at #${index + 1}`);
    }

    if (entry.hash !== expectedHash) {
      errors.push(`Proof hash mismatch at #${index + 1}`);
    }

    previousHash = entry.hash;
  }

  const rebuiltManifestHash = await createPayloadManifestHash({
    manifest: payload.manifest,
    blocks: payload.blocks,
    behavior: payload.behavior,
    dossier: payload.dossier,
  }, payload.proof.chain);

  const manifestHashMatches = rebuiltManifestHash === expectedManifestHash && payload.proof.manifestHash === expectedManifestHash;
  if (!manifestHashMatches) {
    errors.push("Manifest hash mismatch");
  }

  return {
    valid: errors.length === 0,
    manifestHashMatches,
    chainValid: errors.length === 0 || errors.every((item) => item !== "Manifest hash mismatch"),
    errors,
  };
}

export function createSeedBinary(header: MSeedHeader, ciphertext: Uint8Array) {
  const headerBytes = encodeUtf8(JSON.stringify(header));
  const prefix = new Uint8Array(HEADER_PREFIX_BYTES);
  const view = new DataView(prefix.buffer);

  prefix.set(encodeUtf8(MSEED_MAGIC), 0);
  view.setUint16(8, MSEED_VERSION, true);
  view.setUint16(10, 0, true);
  view.setUint32(12, headerBytes.length, true);
  view.setUint32(16, ciphertext.length, true);
  view.setUint32(20, 0, true);

  return concatBytes([prefix, headerBytes, ciphertext]);
}

export function parseSeedBinary(bytes: Uint8Array): ParsedMSeed {
  if (bytes.length < HEADER_PREFIX_BYTES) {
    throw new Error("This file is too small to be a Maple seed.");
  }

  const prefix = bytes.slice(0, HEADER_PREFIX_BYTES);
  const view = new DataView(prefix.buffer, prefix.byteOffset, prefix.byteLength);
  const magic = decodeUtf8(prefix.slice(0, 8));
  if (magic !== MSEED_MAGIC) {
    throw new Error("Invalid .mseed signature.");
  }

  const version = view.getUint16(8, true);
  if (version !== MSEED_VERSION) {
    throw new Error(`Unsupported .mseed version ${version}.`);
  }

  const headerLength = view.getUint32(12, true);
  const ciphertextLength = view.getUint32(16, true);
  const headerStart = HEADER_PREFIX_BYTES;
  const headerEnd = headerStart + headerLength;
  const ciphertextEnd = headerEnd + ciphertextLength;

  if (ciphertextEnd > bytes.length) {
    throw new Error("The .mseed payload is truncated.");
  }

  const header = JSON.parse(decodeUtf8(bytes.slice(headerStart, headerEnd))) as MSeedHeader;
  const ciphertext = bytes.slice(headerEnd, ciphertextEnd);

  return { header, ciphertext, bytes };
}

export async function encryptPayloadForHeader(
  payload: MSeedPayload,
  publicKeyJwk: JsonWebKey,
  keyId: string,
  seal: MSeedSeal,
  passwordProtection?: MSeedPasswordProtection
): Promise<{ header: MSeedHeader; ciphertext: Uint8Array; }> {
  const plaintext = encodeUtf8(JSON.stringify(payload));
  const aesKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, plaintext);
  const rawAesKey = new Uint8Array(await crypto.subtle.exportKey("raw", aesKey));

  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicKeyJwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );

  const wrappedKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, rawAesKey);

  const header: MSeedHeader = {
    format: "mseed",
    version: MSEED_VERSION,
    seedId: payload.manifest.seedId,
    createdAt: payload.manifest.createdAt,
    manifestHash: payload.proof.manifestHash,
    envelope: {
      algorithm: "AES-256-GCM",
      keyWrap: "RSA-OAEP-256",
      ivBase64: bytesToBase64(iv),
      wrappedKeyBase64: bytesToBase64(new Uint8Array(wrappedKey)),
      keyId,
    },
    ...(passwordProtection ? { passwordProtection } : {}),
    seal,
  };

  return {
    header,
    ciphertext: new Uint8Array(encrypted),
  };
}

export async function compileWatToWasmBytes(watSource: string) {
  const wabtFactory = (await import("wabt")).default as any;
  const wabt = await wabtFactory();
  const parsed = wabt.parseWat("seed.wat", watSource);
  const binary = parsed.toBinary({ log: false });
  return new Uint8Array(binary.buffer);
}

export async function instantiateWasmBehavior(behavior: MSeedBehavior) {
  const bytes = base64ToBytes(behavior.wasmBase64);
  const logs: string[] = [];
  const instance = await WebAssembly.instantiate(bytes, {
    env: {
      abort: () => {
        logs.push("WASM abort called");
      },
    },
  });

  return {
    instance,
    exports: instance.instance.exports as Record<string, any>,
    logs,
  };
}

export async function buildSeedPayload(input: {
  title: string;
  summary: string;
  author: string;
  tags: string[];
  source: "manual" | "blueprint" | "hybrid";
  blocks: MSeedBlock[];
  behavior: MSeedBehavior | null;
}) {
  const manifest: MSeedManifest = {
    seedId: crypto.randomUUID(),
    title: input.title.trim(),
    summary: input.summary.trim(),
    createdAt: new Date().toISOString(),
    dossierStyle: "tabs",
    author: input.author.trim() || "MapleOS User",
    tags: input.tags,
    source: input.source,
    blockCount: input.blocks.length,
    hasBehavior: Boolean(input.behavior),
  };

  const draftPayload = {
    manifest,
    blocks: input.blocks,
    behavior: input.behavior,
    dossier: {
      sections: ["overview", "data", "behavior", "proof"] as Array<"overview" | "data" | "behavior" | "proof">,
    },
  };

  const chain = await buildProofChain(input.blocks, input.behavior);
  const manifestHash = await createPayloadManifestHash(draftPayload, chain);

  return {
    manifest,
    blocks: input.blocks,
    behavior: input.behavior,
    proof: {
      manifestHash,
      chain,
    },
    dossier: draftPayload.dossier,
  } satisfies MSeedPayload;
}

export function summarizeBlueprintDraft(blueprint: any) {
  const nodes = Array.isArray(blueprint?.nodes) ? blueprint.nodes : [];
  const edges = Array.isArray(blueprint?.edges) ? blueprint.edges : [];
  const nodeTypes = Array.from(new Set(nodes.map((node: any) => String(node.type || "unknown"))));

  return {
    name: String(blueprint?.name || "Blueprint Draft"),
    description: String(blueprint?.description || ""),
    nodeCount: nodes.length,
    edgeCount: edges.length,
    nodeTypes,
  };
}

export function slugifySeedTitle(title: string) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

  return slug || "maple-seed";
}
